import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Upload, Trophy, User, LogOut, LogIn, Sparkles, Crown, Heart, Compass, Shield } from "lucide-react";
import NotificationBell from "./NotificationBell";
import SearchBar from "./SearchBar";

const TIER_BADGE = {
    pod: { label: "POD", cls: "bg-[#FFB8D1]", Icon: Sparkles },
    elite: { label: "ELITE", cls: "bg-[#FFE55C]", Icon: Crown },
};

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const doLogout = async () => {
        await logout();
        navigate("/");
    };

    const badge = user && TIER_BADGE[user.tier];

    return (
        <nav className="sticky top-0 z-50 bg-[#FFFDF7] border-b-2 border-[#0A0A0A]" data-testid="main-navbar">
            <div className="max-w-5xl mx-auto flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-3">
                <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-home">
                    <div className="w-9 h-9 rounded-lg bg-[#FFE55C] border-2 border-[#0A0A0A] flex items-center justify-center font-black text-lg">
                        10
                    </div>
                    <span className="display font-black text-2xl tracking-tight hidden sm:inline">footsie<span className="text-[#FF5A5F]">.</span></span>
                </Link>

                <div className="hidden md:block flex-1 max-w-md">
                    <SearchBar />
                </div>

                <div className="flex items-center gap-2 sm:gap-3 ml-auto">
                    <Link
                        to="/discover"
                        className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg brut-inset bg-white text-sm font-bold hover:bg-[#B8E1FF] transition-colors"
                        data-testid="nav-discover"
                    >
                        <Compass className="w-4 h-4" />
                    </Link>
                    <Link
                        to="/pricing"
                        className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg brut-inset bg-white text-sm font-bold hover:bg-[#FFB8D1] transition-colors"
                        data-testid="nav-pricing"
                    >
                        <Sparkles className="w-4 h-4" /> <span className="hidden lg:inline">Upgrade</span>
                    </Link>
                    <Link
                        to="/leaderboard"
                        className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg brut-inset bg-white text-sm font-bold hover:bg-[#FFE55C] transition-colors"
                        data-testid="nav-leaderboard"
                    >
                        <Trophy className="w-4 h-4" />
                    </Link>
                    {user ? (
                        <>
                            <NotificationBell />
                            {user.role === "admin" && (
                                <Link
                                    to="/admin"
                                    className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg brut-inset bg-white text-sm font-bold hover:bg-[#FF5A5F] hover:text-white"
                                    data-testid="nav-admin"
                                    aria-label="Moderation"
                                    title="Moderation queue"
                                >
                                    <Shield className="w-4 h-4" />
                                </Link>
                            )}
                            <Link
                                to="/payouts"
                                className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg brut-inset bg-white text-sm font-bold hover:bg-[#FFB8D1]"
                                data-testid="nav-payouts"
                                aria-label="Payouts"
                            >
                                <Heart className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/upload"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg brut bg-[#FFB8D1] text-sm font-bold"
                                data-testid="nav-upload"
                            >
                                <Upload className="w-4 h-4" /> Post
                            </Link>
                            <Link
                                to={`/u/${user.username}`}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg brut-inset bg-white text-sm font-bold"
                                data-testid="nav-profile"
                            >
                                <User className="w-4 h-4" /> {user.username}
                                {badge && (
                                    <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-black border border-[#0A0A0A] ${badge.cls}`} data-testid="nav-tier-badge">
                                        {badge.label}
                                    </span>
                                )}
                            </Link>
                            <button
                                onClick={doLogout}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg brut-inset bg-white text-sm font-bold hover:bg-[#FF5A5F] hover:text-white transition-colors"
                                data-testid="nav-logout"
                                aria-label="Logout"
                            >
                                <LogOut className="w-4 h-4" />
                            </button>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg brut-inset bg-white text-sm font-bold"
                                data-testid="nav-login"
                            >
                                <LogIn className="w-4 h-4" /> Log in
                            </Link>
                            <Link
                                to="/register"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg brut bg-[#0A0A0A] text-white text-sm font-bold"
                                data-testid="nav-register"
                            >
                                Sign up
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </nav>
    );
}

